import {sendEvent} from '@github-ui/hydro-analytics'
import {sendCustomMetric} from '@github-ui/stats'
import {getSelector} from './get-selector'

/**
 * Dead click detection: identifies clicks that produce no visible response.
 *
 * A "dead click" is a click on an element that produces NO:
 * - DOM mutations (no content changes, no loading indicators)
 * - Scroll events (no scroll-into-view or anchor behavior)
 * - Input focus changes
 *
 * This is more actionable than rage clicks because it catches the problem on the
 * FIRST failed interaction, before the user becomes frustrated.
 *
 * Excluded elements (inherently non-interactive or handled by the browser):
 * - Links with href (browser handles navigation)
 * - Form inputs, textareas, selects (clicking focuses them — always produces a response)
 * - Canvas, video, audio (media elements handle their own events)
 * - Contenteditable elements
 * - Elements with role="button", role="link", role="menuitem" (interactive ARIA semantics)
 * - Elements with role="slider", role="scrollbar" (drag-based interaction)
 */

/** Time to wait for page activity after a click before declaring it "dead" */
const DEAD_CLICK_TIMEOUT_MS = 1000

/** Don't report dead clicks on the same selector more than once per cooldown */
const COOLDOWN_MS = 10000

/** How long to keep errors in the buffer before pruning */
const ERROR_BUFFER_TTL_MS = 2000

/** Max errors to keep in the ring buffer */
const ERROR_BUFFER_MAX = 20

/** Elements that should never be considered dead clicks */
const EXCLUDED_TAGS = new Set([
  'input',
  'textarea',
  'select',
  'option',
  'canvas',
  'video',
  'audio',
  'label', // clicking a label focuses its input
])

const EXCLUDED_ROLES = new Set(['slider', 'scrollbar', 'link', 'menuitem', 'menuitemcheckbox', 'menuitemradio'])

function shouldExclude(element: Element): boolean {
  const tag = element.tagName.toLowerCase()
  if (EXCLUDED_TAGS.has(tag)) return true

  // Links with href — browser handles navigation
  if (tag === 'a' && element.hasAttribute('href')) return true

  // Contenteditable elements
  if (element instanceof HTMLElement && element.isContentEditable) return true

  // Interactive ARIA roles — these elements may handle clicks without DOM mutations
  const role = element.getAttribute('role')
  if (role && EXCLUDED_ROLES.has(role)) return true

  // Elements inside excluded containers
  const closestExcluded = element.closest(
    'input, textarea, select, canvas, video, audio, [contenteditable="true"], [role="link"], [role="menuitem"]',
  )
  if (closestExcluded) return true

  return false
}

export function observeDeadClicks(): void {
  const cooldowns = new Map<string, number>()
  const errorBuffer: Array<{timestamp: number; message: string}> = []
  let pendingClick = false

  // Track JS errors that occur near clicks — uses addEventListener to avoid
  // clobbering existing window.onerror handlers
  window.addEventListener('error', event => {
    const message = event.message || ''
    if (!message) return
    errorBuffer.push({timestamp: performance.now(), message})
    // Keep buffer bounded
    if (errorBuffer.length > ERROR_BUFFER_MAX) errorBuffer.shift()
  })

  window.addEventListener('unhandledrejection', event => {
    const message = event.reason?.message || String(event.reason || '')
    if (!message) return
    errorBuffer.push({timestamp: performance.now(), message})
    if (errorBuffer.length > ERROR_BUFFER_MAX) errorBuffer.shift()
  })

  document.addEventListener('click', event => {
    const target = event.target
    if (!target || !(target instanceof Element)) return
    if (shouldExclude(target)) return

    // Only track one click at a time — avoid stacking MutationObservers
    // on rapid clicks, which would observe the full document subtree concurrently
    if (pendingClick) return

    const now = performance.now()
    const selector = getSelector(target)
    if (!selector) return

    // Check cooldown
    const lastReport = cooldowns.get(selector)
    if (lastReport && now - lastReport < COOLDOWN_MS) return

    // Watch for page activity: DOM mutations, scrolls, or focus changes
    let activityDetected = false
    let cleanedUp = false
    pendingClick = true

    const cleanup = () => {
      if (cleanedUp) return
      cleanedUp = true
      pendingClick = false
      mutationObserver.disconnect()
      document.removeEventListener('scroll', onScroll, {capture: true})
      document.removeEventListener('focusin', onFocusIn)
    }

    const onActivity = () => {
      activityDetected = true
      cleanup()
    }

    const mutationObserver = new MutationObserver(onActivity)

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })

    const onScroll = () => {
      onActivity()
    }
    const onFocusIn = () => {
      onActivity()
    }

    // eslint-disable-next-line github/prefer-observers -- detecting scroll activity post-click, not element visibility
    document.addEventListener('scroll', onScroll, {capture: true, once: true})
    document.addEventListener('focusin', onFocusIn, {once: true})

    // Check defaultPrevented after event propagation completes — at this point
    // bubble-phase handlers have had a chance to call preventDefault()
    setTimeout(() => {
      if (event.defaultPrevented) {
        activityDetected = true
        cleanup()
        return
      }
    }, 0)

    setTimeout(() => {
      cleanup()

      if (!activityDetected) {
        cooldowns.set(selector, performance.now())

        // Clean up old cooldowns
        if (cooldowns.size > 50) {
          const cutoff = performance.now() - COOLDOWN_MS
          for (const [key, time] of cooldowns) {
            if (time < cutoff) cooldowns.delete(key)
          }
        }

        // Check if a JS error occurred during this click's lifetime
        const clickEnd = performance.now()
        const errorDuringClick = findErrorDuringClick(errorBuffer, now, clickEnd)

        if (errorDuringClick) {
          reportErrorClick(selector, errorDuringClick)
        } else {
          reportDeadClick(selector)
        }

        // Prune stale errors
        pruneErrorBuffer(errorBuffer, clickEnd)
      }
    }, DEAD_CLICK_TIMEOUT_MS)
  })
}

function reportDeadClick(selector: string): void {
  sendEvent(
    'dead-click',
    {
      target: selector.slice(0, 200),
      url: window.location.href,
    },
    {batched: true},
  )

  sendCustomMetric(
    {
      name: 'BROWSER_VITALS_COUNT_DEAD_CLICK',
      value: 1,
      requestUrl: window.location.href,
    },
    false,
    1, // Low-frequency event, no additional sampling needed
  )
}

function sanitizeErrorMessage(message: string): string {
  // Extract only the error type/name (e.g. "TypeError", "ReferenceError") to avoid
  // sending potentially sensitive user data in error messages
  const colonIndex = message.indexOf(':')
  if (colonIndex > 0 && colonIndex < 50) {
    const errorType = message.slice(0, colonIndex).trim()
    // Only keep the type if it looks like an error class name (alphanumeric, no spaces)
    if (/^[A-Za-z][A-Za-z0-9]*$/.test(errorType)) {
      return errorType
    }
  }
  return 'Error'
}

function reportErrorClick(selector: string, errorMessage: string): void {
  sendEvent(
    'error-click',
    {
      target: selector.slice(0, 200),
      errorType: sanitizeErrorMessage(errorMessage),
      url: window.location.href,
    },
    {batched: true},
  )

  sendCustomMetric(
    {
      name: 'BROWSER_VITALS_COUNT_ERROR_CLICK',
      value: 1,
      requestUrl: window.location.href,
    },
    false,
    1, // Low-frequency event, no additional sampling needed
  )
}

function findErrorDuringClick(
  buffer: Array<{timestamp: number; message: string}>,
  clickStart: number,
  clickEnd: number,
): string | undefined {
  for (const entry of buffer) {
    if (entry.timestamp >= clickStart && entry.timestamp <= clickEnd) {
      return entry.message
    }
  }
  return undefined
}

function pruneErrorBuffer(buffer: Array<{timestamp: number; message: string}>, now: number): void {
  const cutoff = now - ERROR_BUFFER_TTL_MS
  while (buffer.length > 0 && buffer[0] && buffer[0].timestamp < cutoff) {
    buffer.shift()
  }
}
